import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box, Button, Checkbox, Chip, CircularProgress, Container, Dialog, DialogActions,
  DialogContent, DialogContentText, DialogTitle, FormControl, IconButton, InputAdornment,
  InputLabel, ListItemText, MenuItem, OutlinedInput, Paper, Select, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import CloseIcon from "@mui/icons-material/Close";
import InventoryOutlinedIcon from "@mui/icons-material/InventoryOutlined";
import StarIcon from "@mui/icons-material/Star";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import { useNotifier } from "../../../Core/Notifier";
import { api, T, inputSx, fmtCAD, PRICING_TYPES, PRICING_LABELS } from "../ServicesManager/api";
import { uploadImage } from "../../../Components/UploadFile/upload";
import { API_URL } from "../../../config";
import ItemProfileDrawer from "./ItemProfileDrawer";

const buildImageUrl = (path: string | null | undefined): string | null => {
  if (!path) return null;
  if (path.startsWith("http")) return path;
  return `${API_URL}/../${path}`;
};

/* ═══════════════════════════════════════════════════════════════════════════
   ADD/EDIT ITEM MODAL
   ═══════════════════════════════════════════════════════════════════════════ */
const ItemFormModal: React.FC<{
  open: boolean; item: any | null; services: any[];
  onSave: (data: any) => void; onClose: () => void; saving: boolean;
}> = ({ open, item, services, onSave, onClose, saving }) => {
  const [name, setName] = useState("");
  const [serviceId, setServiceId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [subcategoryId, setSubcategoryId] = useState("");
  const [unit, setUnit] = useState("");
  const [defaultPrice, setDefaultPrice] = useState("");
  const [supportedPricingTypes, setSupportedPricingTypes] = useState<string[]>([]);
  const [status, setStatus] = useState("active");
  const [description, setDescription] = useState("");
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  // Load categories/subcategories for selected service from tree
  const [tree, setTree] = useState<any[]>([]);
  const [treeLoading, setTreeLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setName(item?.name || "");
      setServiceId(item?.service_id?.toString() || "");
      setCategoryId(item?.category_id?.toString() || "");
      setSubcategoryId(item?.subcategory_id?.toString() || "");
      setUnit(item?.unit || "");
      setDefaultPrice(item?.default_price?.toString() || "");
      setSupportedPricingTypes(item?.supported_pricing_types ?? []);
      setStatus(item?.status || "active");
      setDescription(item?.description || "");
      setImageUrl(item?.image_url || null);
    }
  }, [item, open]);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return;
    if (file.size > 2 * 1024 * 1024) return;
    setUploading(true);
    try {
      const path = await uploadImage(file);
      if (path) setImageUrl(path);
    } catch { /* ignore */ }
    setUploading(false);
  };

  useEffect(() => {
    if (!serviceId) { setTree([]); return; }
    setTreeLoading(true);
    api.get(`/banquet/services-list/${serviceId}`).then((res) => {
      if (res.code === 200) setTree(res.data?.categories ?? []);
      setTreeLoading(false);
    });
  }, [serviceId]);

  const categories = tree;
  const subcategories = useMemo(() => {
    if (!categoryId) return [];
    const cat = tree.find((c: any) => c.id === Number(categoryId));
    return cat?.subcategories ?? [];
  }, [tree, categoryId]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "16px", overflow: "hidden" } }}>
      <Box sx={{ px: 3, py: 2, background: `linear-gradient(135deg, ${T.navy} 0%, #1a3550 100%)`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
          <Box sx={{ width: 32, height: 32, borderRadius: "10px", bgcolor: "rgba(201,169,110,0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <InventoryOutlinedIcon sx={{ fontSize: 16, color: T.gold }} />
          </Box>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: T.white }}>{item?.id ? "Edit Item" : "New Item"}</Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: "rgba(255,255,255,0.6)" }}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
      </Box>

      <DialogContent sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }}>
        <TextField label="Item Name *" size="small" value={name} onChange={(e) => setName(e.target.value)} autoFocus fullWidth sx={inputSx} />

        {/* Image Upload */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {imageUrl ? (
            <Box sx={{ position: "relative", width: 80, height: 60, borderRadius: "10px", overflow: "hidden", border: `1px solid ${T.line}` }}>
              <Box component="img" src={buildImageUrl(imageUrl) || ""} alt="Item" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
              <IconButton size="small" onClick={() => setImageUrl(null)} sx={{
                position: "absolute", top: 1, right: 1, bgcolor: "rgba(0,0,0,0.5)", width: 18, height: 18,
                "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
              }}>
                <CloseIcon sx={{ fontSize: 12, color: "#fff" }} />
              </IconButton>
            </Box>
          ) : (
            <Box sx={{
              width: 80, height: 60, borderRadius: "10px", border: `1.5px dashed ${T.line}`,
              display: "flex", alignItems: "center", justifyContent: "center", bgcolor: T.bg,
            }}>
              <ImageOutlinedIcon sx={{ fontSize: 22, color: T.slateLt }} />
            </Box>
          )}
          <Box sx={{ flex: 1 }}>
            <Button component="label" size="small" variant="outlined" disabled={uploading}
              startIcon={uploading ? <CircularProgress size={12} /> : <CloudUploadOutlinedIcon sx={{ fontSize: 14 }} />}
              sx={{ textTransform: "none", fontSize: 11, fontWeight: 600, borderColor: T.line, color: T.slate, borderRadius: "8px" }}>
              {uploading ? "Uploading..." : imageUrl ? "Change Image" : "Upload Image"}
              <input type="file" accept="image/*" hidden onChange={handleImageUpload} />
            </Button>
            <Typography sx={{ fontSize: 10, color: T.slateLt, mt: 0.3 }}>JPG, PNG, WebP. Max 2MB.</Typography>
          </Box>
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          <TextField select label="Service *" size="small" value={serviceId} onChange={(e) => { setServiceId(e.target.value); setCategoryId(""); setSubcategoryId(""); }} sx={inputSx}>
            <MenuItem value="" disabled>Select service...</MenuItem>
            {services.map((s: any) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
          </TextField>
          <TextField select label="Category" size="small" value={categoryId} onChange={(e) => { setCategoryId(e.target.value); setSubcategoryId(""); }} sx={inputSx} disabled={treeLoading || categories.length === 0}>
            <MenuItem value="">None</MenuItem>
            {categories.map((c: any) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
          </TextField>
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          <TextField select label="Subcategory" size="small" value={subcategoryId} onChange={(e) => setSubcategoryId(e.target.value)} sx={inputSx} disabled={subcategories.length === 0}>
            <MenuItem value="">None</MenuItem>
            {subcategories.map((s: any) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
          </TextField>
          <TextField label="Unit" size="small" placeholder="e.g. per_plate, per_event" value={unit} onChange={(e) => setUnit(e.target.value)} sx={inputSx} />
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          <TextField label="Default Price" size="small" type="number" value={defaultPrice} onChange={(e) => setDefaultPrice(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} sx={inputSx} />
          <TextField select label="Status" size="small" value={status} onChange={(e) => setStatus(e.target.value)} sx={inputSx}>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </TextField>
        </Box>

        <TextField label="Description" size="small" value={description} onChange={(e) => setDescription(e.target.value)} fullWidth multiline rows={2} sx={inputSx} />

        <FormControl size="small" fullWidth sx={inputSx}>
          <InputLabel sx={{ fontSize: 13 }}>Supported Pricing Types</InputLabel>
          <Select
            multiple
            value={supportedPricingTypes}
            onChange={(e) => setSupportedPricingTypes(typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value as string[])}
            input={<OutlinedInput label="Supported Pricing Types" />}
            renderValue={(selected) => (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {(selected as string[]).map((v) => (
                  <Chip key={v} label={PRICING_TYPES.find((pt) => pt.value === v)?.label ?? v} size="small"
                    sx={{ height: 20, fontSize: 11, fontWeight: 600, bgcolor: `${T.gold}15`, color: T.gold }} />
                ))}
              </Box>
            )}
          >
            {PRICING_TYPES.map((pt) => (
              <MenuItem key={pt.value} value={pt.value}>
                <Checkbox checked={supportedPricingTypes.includes(pt.value)} size="small" sx={{ py: 0 }} />
                <ListItemText primary={pt.label} primaryTypographyProps={{ fontSize: 13 }} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>

      <Box sx={{ px: 3, py: 2, display: "flex", justifyContent: "flex-end", gap: 1.5, borderTop: `1px solid ${T.line}` }}>
        <Button onClick={onClose} sx={{ textTransform: "none", fontSize: 13, color: T.slate }}>Cancel</Button>
        <Button variant="contained" disabled={saving || !name.trim() || !serviceId}
          onClick={() => onSave({
            id: item?.id, name: name.trim(), service_id: Number(serviceId),
            category_id: categoryId ? Number(categoryId) : null,
            subcategory_id: subcategoryId ? Number(subcategoryId) : null,
            unit: unit || null, default_price: defaultPrice ? Number(defaultPrice) : null,
            supported_pricing_types: supportedPricingTypes.length > 0 ? supportedPricingTypes : null,
            status, description: description || null, image_url: imageUrl || null,
          })}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveAltIcon sx={{ fontSize: 16 }} />}
          sx={{ textTransform: "none", fontSize: 13, fontWeight: 600, bgcolor: T.navy, borderRadius: "10px", px: 3, "&:hover": { bgcolor: "#1a3550" } }}
        >
          {saving ? "Saving..." : "Save Item"}
        </Button>
      </Box>
    </Dialog>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   ITEMS LIST PAGE
   ═══════════════════════════════════════════════════════════════════════════ */
const ItemsListPage: React.FC = () => {
  const { showMessage, showErrorMessage } = useNotifier();
  const [items, setItems] = useState<any[]>([]);
  const [services, setServices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [filterService, setFilterService] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  // Modals
  const [formOpen, setFormOpen] = useState(false);
  const [editItem, setEditItem] = useState<any | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: number; name: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Profile drawer
  const [profileItemId, setProfileItemId] = useState<number | null>(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filterService) params.append("service_id", filterService);
    if (filterStatus) params.append("status", filterStatus);
    if (search) params.append("search", search);
    const qs = params.toString() ? `?${params.toString()}` : "";

    const [itemsRes, svcRes] = await Promise.all([
      api.get(`/banquet/items-list${qs}`),
      api.get("/banquet/services-list"),
    ]);
    if (itemsRes.code === 200) setItems(itemsRes.data ?? []);
    if (svcRes.code === 200) setServices(svcRes.data ?? []);
    setLoading(false);
  }, [filterService, filterStatus, search]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleSave = async (data: any) => {
    setSaving(true);
    const res = await api.post("/banquet/service-tree/item", data);
    if (res.code === 200) {
      showMessage(data.id ? "Item updated" : "Item created");
      setFormOpen(false);
      setEditItem(null);
      fetchItems();
    } else showErrorMessage(res.message);
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    const res = await api.del(`/banquet/service-tree/item/${deleteTarget.id}`);
    if (res.code === 200) { showMessage("Item deleted"); fetchItems(); }
    else showErrorMessage(res.message);
    setIsDeleting(false);
    setDeleteTarget(null);
  };

  const thSx = { fontWeight: 700, fontSize: 11, color: T.slate, textTransform: "uppercase", letterSpacing: "0.05em", py: 1.5 } as const;

  return (
    <Container maxWidth={false} disableGutters sx={{ px: { xs: 1.5, sm: 2, md: 3, lg: 4 }, pb: 6 }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, pt: 1 }}>
        <Box>
          <Typography sx={{ fontSize: { xs: 20, md: 24 }, fontWeight: 700, color: T.navy, letterSpacing: "-0.02em" }}>Items</Typography>
          <Typography sx={{ fontSize: 13, color: T.slateLt, mt: 0.3 }}>All billable items across every service</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditItem(null); setFormOpen(true); }}
          sx={{ textTransform: "none", fontSize: 13, fontWeight: 600, bgcolor: T.navy, borderRadius: "10px", px: 2.5, boxShadow: "none", "&:hover": { bgcolor: "#1a3550" } }}>
          Add Item
        </Button>
      </Box>

      {/* Filters */}
      <Box sx={{ display: "flex", gap: 1.5, mb: 2.5, flexWrap: "wrap" }}>
        <TextField size="small" placeholder="Search items..." value={search} onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: T.slateLt }} /></InputAdornment> }}
          sx={{ ...inputSx, minWidth: 260 }} />
        <TextField select size="small" value={filterService} onChange={(e) => setFilterService(e.target.value)}
          label="Service" sx={{ ...inputSx, minWidth: 180 }}>
          <MenuItem value="">All Services</MenuItem>
          {services.map((s: any) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
        </TextField>
        <TextField select size="small" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
          label="Status" sx={{ ...inputSx, minWidth: 130 }}>
          <MenuItem value="">All</MenuItem>
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="inactive">Inactive</MenuItem>
        </TextField>
        {(filterService || filterStatus || search) && (
          <Button size="small" onClick={() => { setSearch(""); setFilterService(""); setFilterStatus(""); }}
            sx={{ textTransform: "none", fontSize: 11, color: T.slateLt }}>Clear</Button>
        )}
      </Box>

      {/* Table */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}><CircularProgress sx={{ color: T.gold }} /></Box>
      ) : items.length === 0 ? (
        <Paper elevation={0} sx={{ p: 4, textAlign: "center", borderRadius: "14px", border: `1px solid ${T.line}` }}>
          <InventoryOutlinedIcon sx={{ fontSize: 40, color: T.slateLt, mb: 1 }} />
          <Typography sx={{ fontSize: 14, color: T.slateLt }}>No items found.</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: "14px", border: `1px solid ${T.line}` }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: T.bg }}>
                <TableCell sx={{ ...thSx, width: 40 }}>#</TableCell>
                <TableCell sx={thSx}>Item</TableCell>
                <TableCell sx={thSx}>Service</TableCell>
                <TableCell sx={thSx}>Category</TableCell>
                <TableCell sx={thSx}>Subcategory</TableCell>
                <TableCell sx={thSx}>Default Vendor</TableCell>
                <TableCell align="right" sx={thSx}>Price</TableCell>
                <TableCell align="center" sx={thSx}>Vendors</TableCell>
                <TableCell align="center" sx={thSx}>Status</TableCell>
                <TableCell align="right" sx={thSx}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item: any, idx: number) => {
                const defVP = item.default_vendor_price;
                return (
                  <TableRow key={item.id} hover sx={{ cursor: "pointer", "&:hover": { bgcolor: "#fef6ee" } }}
                    onClick={() => setProfileItemId(item.id)}>
                    <TableCell sx={{ py: 1.2, fontSize: 12, color: T.slateLt }}>{idx + 1}</TableCell>
                    <TableCell sx={{ py: 1.2 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                        {item.image_url ? (
                          <Box component="img" src={buildImageUrl(item.image_url) || ""} alt="" sx={{
                            width: 36, height: 36, borderRadius: "8px", objectFit: "cover",
                            border: `1px solid ${T.line}`, flexShrink: 0,
                          }} />
                        ) : (
                          <Box sx={{
                            width: 36, height: 36, borderRadius: "8px", bgcolor: T.bg,
                            border: `1px solid ${T.line}`, display: "flex", alignItems: "center",
                            justifyContent: "center", flexShrink: 0,
                          }}>
                            <ImageOutlinedIcon sx={{ fontSize: 16, color: T.slateLt }} />
                          </Box>
                        )}
                        <Box>
                          <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.navy }}>{item.name}</Typography>
                          {item.unit && <Typography sx={{ fontSize: 10, color: T.slateLt }}>{item.unit}</Typography>}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 1.2 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: item.service?.color ?? T.slateLt }} />
                        <Typography sx={{ fontSize: 12, color: T.slate }}>{item.service?.name ?? "—"}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 1.2, fontSize: 12, color: T.slate }}>{item.category?.name ?? "—"}</TableCell>
                    <TableCell sx={{ py: 1.2, fontSize: 12, color: T.slate }}>{item.subcategory?.name ?? "—"}</TableCell>
                    <TableCell sx={{ py: 1.2 }}>
                      {defVP ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <StarIcon sx={{ fontSize: 11, color: T.gold }} />
                          <Typography sx={{ fontSize: 12, color: T.navy }}>{defVP.vendor?.name ?? "—"}</Typography>
                        </Box>
                      ) : <Typography sx={{ fontSize: 12, color: T.slateLt }}>—</Typography>}
                    </TableCell>
                    <TableCell align="right" sx={{ py: 1.2 }}>
                      <Typography sx={{ fontSize: 12, fontWeight: 600, color: item.default_price != null ? T.greenTx : T.slateLt }}>
                        {item.default_price != null ? fmtCAD(Number(item.default_price)) : "—"}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ py: 1.2 }}>
                      <Chip label={(item.vendor_prices ?? []).length} size="small" sx={{ height: 20, fontSize: 11, fontWeight: 600, bgcolor: `${T.gold}12`, color: T.gold }} />
                    </TableCell>
                    <TableCell align="center" sx={{ py: 1.2 }}>
                      <Chip label={item.status} size="small" sx={{ height: 20, fontSize: 10, fontWeight: 600, bgcolor: item.status === "active" ? T.greenBg : T.redBg, color: item.status === "active" ? T.greenTx : T.redTx }} />
                    </TableCell>
                    <TableCell align="right" sx={{ py: 1.2 }} onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="View"><IconButton size="small" onClick={() => setProfileItemId(item.id)} sx={{ color: T.gold }}><VisibilityOutlinedIcon sx={{ fontSize: 15 }} /></IconButton></Tooltip>
                      <Tooltip title="Edit"><IconButton size="small" onClick={() => { setEditItem(item); setFormOpen(true); }} sx={{ color: T.slateLt }}><EditOutlinedIcon sx={{ fontSize: 15 }} /></IconButton></Tooltip>
                      <Tooltip title="Delete"><IconButton size="small" onClick={() => setDeleteTarget({ id: item.id, name: item.name })} sx={{ color: T.slateLt }}><DeleteOutlineIcon sx={{ fontSize: 15 }} /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Modals */}
      <ItemFormModal open={formOpen} item={editItem} services={services} onSave={handleSave} onClose={() => { setFormOpen(false); setEditItem(null); }} saving={saving} />

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" PaperProps={{ sx: { borderRadius: "14px" } }}>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: 16 }}><WarningAmberIcon color="error" /> Delete item?</DialogTitle>
        <DialogContent><DialogContentText sx={{ fontSize: 13 }}>Delete <strong>{deleteTarget?.name}</strong>?</DialogContentText></DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteTarget(null)} sx={{ textTransform: "none", color: T.slate }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={isDeleting} sx={{ textTransform: "none", fontWeight: 600, borderRadius: "8px" }}>{isDeleting ? "..." : "Delete"}</Button>
        </DialogActions>
      </Dialog>

      {/* Item Profile Drawer */}
      <ItemProfileDrawer itemId={profileItemId} onClose={() => setProfileItemId(null)} onRefresh={fetchItems} />
    </Container>
  );
};

export default ItemsListPage;
